<template>
  <v-container fluid>
    <gg-card class="mt-6">
      <gg-card-text class="px-4 py-10">
        <v-row v-if="loading || error || deployError">
          <v-col cols="12" class="text-center" v-if="loading">
            <h2 class="text--black">
              {{ finalText.loading_title }}
            </h2>

            <v-progress-circular indeterminate
                                 width="2"
                                 size="64"
                                 color="primary"
                                 class="my-7"
            />
            <p>
              {{ finalText.loading_text }}
            </p>
          </v-col>
          <v-col cols="12" v-if="error">
            <v-row class="text-center">
              <v-col cols="12">
                <h2 class="text--black">
                  {{ finalText.error_title }}
                </h2>
              </v-col>
              <v-col cols="12">
                <v-icon color="red" size="180">
                  mdi-close-circle
                </v-icon>
              </v-col>
              <v-col cols="12">
                {{ finalText.error_text }}
              </v-col>
              <v-col cols="12">
                <gg-btn @click="setStep(2)">
                  Back
                </gg-btn>
              </v-col>
              </v-row>
          </v-col>
          <v-col cols="12" v-if="deployError" class="text-center">
            <v-row>
              <v-col cols="12" >
                <h2 class="text--black">
                  Warning: Content Exists
                </h2>
              </v-col>
              <v-col cols="12" >
                <v-icon color="red" size="180">
                  mdi-close-circle
                </v-icon>
              </v-col>
              <v-col cols="12" >
                Content currently exists on your domain {{ second_step.domains[0] }}. Would you like to overwrite the existing
                content?
              </v-col>
              <v-col cols="12">
                <v-checkbox class="d-inline-flex text-center"
                            label="I understand that this step is irreversible. Go ahead and overwrite the existing content."
                            v-model="overwriteApplication"
                />
              </v-col>
              <v-col cols="12">
                <gg-btn @click="setStep(2)">
                  Go Back
                </gg-btn>
                <gg-btn :disabled="!overwriteApplication" class="mx-1" color="primary" @click="submit()">
                  Continue
                </gg-btn>
              </v-col>
            </v-row>
          </v-col>
        </v-row>
        <v-row v-else class="text-center">
          <v-col cols="12">
            <v-row>
              <v-col cols="12">
                <h2 class="text--black">
                  {{ finalText.success_title }}
                </h2>
              </v-col>
              <v-col cols="12">
                <v-icon color="primary" size="180">
                  mdi-checkbox-marked-circle
                </v-icon>
              </v-col>
              <v-col cols="12" v-html="finalText.success_text" />
              <v-col cols="12" v-if="application.flow === 'wordpress'">
                <span class="font-weight-bold">Email: </span>{{ email }} <br>
                <span class="font-weight-bold">Password: </span>{{ password }}
                <v-tooltip top>
                  <template v-slot:activator="{ on, attrs }">
                    <gg-btn icon color="primary" x-small @click.stop.prevent="copyPassword" v-bind="attrs" v-on="on">
                      <v-icon>mdi-clipboard-text</v-icon>
                    </gg-btn>
                  </template>
                  <span>Copy Password</span>
                </v-tooltip>
                <input type="hidden" id="app-password" :value="password">
              </v-col>
              <v-col cols="12">
                <div v-if="path === 'New Website'">
                  <gg-btn tile @click="manageService">Manage Account</gg-btn>
                  <gg-btn color="primary" class="mx-1" @click="openLink" tile>
                    {{ finalText.btn_text }}
                  </gg-btn>
                </div>
                <gg-btn v-if="path === 'Migration' || path === 'Starter Site'"
                       tile
                       color="primary"
                       :to="{ name: 'ticket_view', params: { ticketid: this.ticket_id }}">
                  {{ finalText.btn_text }}
                </gg-btn>
              </v-col>
            </v-row>
          </v-col>
        </v-row>
      </gg-card-text>
    </gg-card>
  </v-container>
</template>

<script>
import { mapGetters, mapActions, mapMutations } from 'vuex'

export default {
  name: 'WizardStepThree',
  data: () => ({
    loading: false,
    error: false,
    deployError: false,
    email: '',
    password: ''
  }),
  computed: {
    ...mapGetters({
      path: 'wizard_model/path',
      application: 'wizard_model/application',
      wordpress: 'wizard_model/wordpress',
      ticket_id: 'wizard/ticket_id',
      url: 'wizard/url',
      second_step: 'wizard/second_step',
      service_id: 'wizard/service_id'
    }),
    finalText () {
      const case1 = this.path === 'New Website' && (this.application?.flow === 'wordpress')
      const case2 = this.path === 'New Website' && (this.application?.flow === 'sitebuilder')
      const case3 = this.path === 'Migration'
      const caseStarterSite = this.path === 'Starter Site'

      if (case1) {
        return {
          btn_text: 'Go to WP Dashboard',
          loading_title: 'Deploying Website',
          loading_text: 'We are deploying your new website. Please wait a moment.',
          success_title: 'Congrats, your website is ready!',
          success_text: `Your new WordPress website is now ready for you. Click the button below to get started. You can also access the admin area by going to <a href="${this.url}">${this.url}</a>`,
          error_title: 'Deployment Error',
          error_text: 'An error occurred during website deployment. Please go back and try again or contact support.'
        }
      } else if (case2) {
        const captions = {
          btn_text: 'Go To Admin Area',
          loading_title: 'Deploying Website',
          loading_text: 'Your new project is being deployed. Please wait a moment.',
          success_title: 'SitePad Website Builder Activated!',
          success_text: 'Begin building your website! Click the button below to access the SitePad website builder.',
          error_title: 'Deployment Error',
          error_text: 'There was a problem setting up your project. Please go back or contact support for assistance.'
        }
        return captions
      } else if (case3) {
        return {
          btn_text: 'Review Migration Request',
          loading_title: 'Submitting Request',
          loading_text: 'We are submitting your migration request. Please wait a moment.',
          success_title: 'Request Received',
          success_text: 'Your migration request has been received. Click the button below to review your request.',
          error_title: 'Request Error',
          error_text: 'There was a problem submitting your migration request. Please go back or contact support for assistance.'
        }
      } else if (caseStarterSite) {
        return {
          btn_text: 'Review Starter Site Request',
          loading_title: 'Submitting Request',
          loading_text: 'We are submitting your Starter Site request. Please wait a moment.',
          success_title: 'Request Received',
          success_text: 'Your Starter Site request has been received. Click the button below to review your request.',
          error_title: 'Request Error',
          error_text: 'There was a problem submitting your Starter Site. Please go back or contact support for assistance.'
        }
      } else {
        return ''
      }
    },
    overwriteApplication: {
      get () {
        return this.wordpress.overwrite
      },
      set (val) {
        this.setOverwrite(val)
      }
    }
  },
  async created () {
    await this.submit()
  },
  methods: {
    ...mapMutations({
      setStep: 'wizard/SET_STEP',
      setOverwrite: 'wizard_model/SET_OVERWRITE'
    }),
    ...mapActions({
      navigateTo: 'navigateTo',
      submitData: 'wizard/submitData',
      getServices: 'getServices',
      reportSuccess: 'reportSuccess',
      reportError: 'reportError'
    }),
    openLink () {
      window.open(this.url, '_self')
    },
    copyPassword () {
      // app-password
      const pwdCopy = document.querySelector('#app-password')
      pwdCopy.setAttribute('type', 'text')
      pwdCopy.select()
      const successful = document.execCommand('copy')
      if (successful) {
        this.reportSuccess('Password copied to clipboard.')
      }
      pwdCopy.setAttribute('type', 'hidden')
      window.getSelection().removeAllRanges()
    },
    manageService () {
      this.navigateTo(this.service_id)
    },
    async submit (overwrite = false) {
      try {
        this.error = false
        this.deployError = false
        this.loading = true
        if (this.path === 'New Website') {
          const {
            deployed,
            applicationExists,
            resultmsg,
            email,
            password
          } = await this.submitData()

          if (deployed) {
            this.email = email
            this.password = password
            this.reportSuccess(resultmsg)
            await this.getServices()
          } else {
            if (applicationExists) {
              // this.loading = false
              this.deployError = true
            } else {
              // this.loading = false
              this.error = true
              this.reportError(resultmsg)
            }
          }
        } else {
          const msg = await this.submitData()
          this.reportSuccess(msg)
          await this.getServices()
        }
      } catch (e) {
        this.error = true
        this.reportError(e)
      } finally {
        this.loading = false
      }
    }
  }
}
</script>
